/**
 * AuthGate — top-level guard for `/app/*` routes. Produces the canonical
 * UX for each `me-context` state per `docs/frontend/035-frontend-app-shell-plan.md §3.2`:
 *   - `idle` / `loading`: a session transition surface that NAMES what it is
 *     waiting for. It was a title-less skeleton until Task 306A §4.4; that is
 *     the surface an activated owner watches while `me/context` is re-read,
 *     and it rendered no text at all.
 *   - `unauthenticated`: redirect to `/login?next=<path>`.
 *   - `no-tenant`: render the "no tenant access" state with sign-out.
 *   - `error`: render an envelope-aware error state with retry.
 *   - `ready`: render the protected children.
 *
 * Sysadmin handling lives inside the rendered children (`AppShell`) per
 * §2.3 / §5.4 because the sidebar still renders a banner for them.
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useMeContext } from '../../context/MeContextProvider';
import { ErrorState, PermissionDeniedState, SessionTransitionState } from '../states';

interface AuthGateProps {
  readonly children: ReactNode;
}

/**
 * Task 306A §4.4 — the session skeleton is the surface an activated owner
 * looks at while `me/context` is re-read, so it has to SAY something.
 * Exported so the copy is asserted against the rendered gate rather than
 * quoted from it.
 */
export const AUTH_SESSION_TRANSITION_COPY = {
  title: 'جاري تجهيز الجلسة…',
  description: 'نقرأ بيانات حسابك وصلاحياته الآن. أبقِ هذه الصفحة مفتوحة.',
} as const;

export function AuthGate({ children }: AuthGateProps) {
  const { status, refresh, signOut, error } = useMeContext();
  const location = useLocation();

  if (status === 'idle' || status === 'loading') {
    // NOT `LoadingState`: its default `page` variant drops the title, which
    // is what made this exact surface a text-free document for the whole
    // pending_activation → activated transition (§4.4).
    return (
      <SessionTransitionState
        title={AUTH_SESSION_TRANSITION_COPY.title}
        description={AUTH_SESSION_TRANSITION_COPY.description}
        testId="auth-session-transition"
      />
    );
  }

  if (status === 'unauthenticated') {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (status === 'no-tenant') {
    return (
      <div style={{ padding: '64px 24px', maxWidth: 560, margin: '0 auto' }}>
        <PermissionDeniedState
          title="لا يوجد حساب فعّال على هذه المساحة"
          description="هذا الحساب غير مرتبط بأي مؤسسة فعّالة. تواصل مع Owner الوكالة لتفعيل الوصول."
          action={
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                void signOut();
              }}
            >
              تسجيل الخروج
            </button>
          }
        />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ padding: '64px 24px', maxWidth: 560, margin: '0 auto' }}>
        <ErrorState
          title="تعذر تحميل بيانات الجلسة"
          description="حدث خطأ أثناء قراءة me-context. تحقق من اتصال الإنترنت أو حاول لاحقاً."
          error={error}
          onRetry={refresh}
        />
      </div>
    );
  }

  return <>{children}</>;
}
